import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Suspense, useEffect, useState } from "react";
import { loadResourceBundle, supportedLngs } from "@tablemint/i18n";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import HomePage from "./pages/Home";
import Features from "./pages/Features";
import PricingPage from "./pages/Pricing";
import About from "./pages/About";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import WidgetConfigurator from "./pages/WidgetConfigurator";
import Reservations from "./pages/Reservations";
import Settings from "./pages/Settings";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { Toaster } from "./components/ui/toast";
import BookingPage from "./pages/BookingPage";

// Loading component for suspense fallback
const LoadingScreen = () => (
  <div className="flex h-screen w-screen items-center justify-center">
    <div className="animate-pulse text-primary">Loading...</div>
  </div>
);

const App = () => {
  const [translationsLoaded, setTranslationsLoaded] = useState(false);

  // Load translation resources on initial render
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        // Load all necessary translation bundles for all supported languages
        const loadPromises = [];

        for (const lng of supportedLngs) {
          loadPromises.push(loadResourceBundle(lng, "common"));
          loadPromises.push(loadResourceBundle(lng, "landing"));
          loadPromises.push(loadResourceBundle(lng, "about"));
          loadPromises.push(loadResourceBundle(lng, "legal"));
        }

        await Promise.all(loadPromises);

        // Force an update to ensure translations are applied
        window.dispatchEvent(new Event("i18nextLanguageChanged"));

        // Set translations as loaded
        setTranslationsLoaded(true);
      } catch (error) {
        console.error("Failed to load translations:", error);
        // Set as loaded anyway to prevent infinite loading screen
        setTranslationsLoaded(true);
      }
    };

    loadTranslations();
  }, []);

  if (!translationsLoaded) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/sign-in" element={<Login />} />
              <Route path="/sign-up" element={<Signup />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/book/:businessId" element={<BookingPage />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/widget-configurator"
                element={
                  <ProtectedRoute>
                    <WidgetConfigurator />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reservations"
                element={
                  <ProtectedRoute>
                    <Reservations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />

              {/* Catch-all route */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <Toaster />
      </div>
    </Router>
  );
};

export default App;
